import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

const EarningsPage = () => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showIntroModal, setShowIntroModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showModalInfo, setShowModalInfo] = useState(false);
  const [showModalHow, setShowModalHow] = useState(false);
  const [isActivated, setIsActivated] = useState(false);
  const [consentGiven, setConsentGiven] = useState(false);
  const navigate = useNavigate();
  const [monetizationProfile, setMonetizationProfile] = useState(null);

  useEffect(() => {
    const checkActivation = async () => {
      try {
        const res = await fetch("/api/monetizacion/status/", {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access")}`,
          },
        });
        const result = await res.json();
        setIsActivated(result.activated);
      } catch (err) {
        console.error("Error al verificar activación:", err);
      }
    };
    checkActivation();
  }, []);

    useEffect(() => {
      const fetchProfile = async () => {
        try {
          const res = await fetch("/api/monetizacion/profile/", {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("access")}`,
            },
          });
          if (!res.ok) throw new Error("Error al cargar perfil");
          const result = await res.json();
          setMonetizationProfile(result);
        } catch (err) {
          console.error("Error al cargar el perfil de monetización:", err);
        }
      };

      fetchProfile();
    }, []);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await fetch("/api/ads/stats/", {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access")}`,
          },
        });
        if (!res.ok) throw new Error();
        const result = await res.json();
        setData(result);
      } catch {
        console.error("Error al cargar estadísticas");
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  const activarMonetizacion = async () => {
    try {
      const res = await fetch("/api/monetizacion/activate/", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access")}`,
          "Content-Type": "application/json",
        },
      });
      if (res.ok) {
        setIsActivated(true);
        setShowIntroModal(false);
      }
    } catch (err) {
      console.error("Error al activar:", err);
    }
  };

  const desactivarMonetizacion = async () => {
    try {
      const res = await fetch("/api/monetizacion/activate/", {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access")}`,
        },
      });
      if (res.ok) {
        navigate("/monetizacion");
      }
    } catch (err) {
      console.error("Error:", err);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-60 text-gray-500 animate-pulse">
        {t("earnings.loading")}
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center text-red-600 p-4">
        {t("earnings.error")}
      </div>
    );
  }


  const availableTokens = parseFloat(monetizationProfile?.tokens_acumulados || "0");
  const canClaim = availableTokens >= 100;

  return (
    <>
    {/* Modal: ¿Qué es la monetización? */}
    {showModalInfo && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center pt-24 z-50">
        <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-lg text-gray-800">
          <h2 className="text-2xl font-semibold mb-4 text-center">
            {t("monetization.info_title")}
          </h2>
          <p className="text-base mb-4 font-semibold text-yellow-600">{t("monetization.info_text")}</p>
          <div className="text-right">
            <button
              onClick={() => setShowModalInfo(false)}
              className="bg-blue-600 text-white px-5 py-2 rounded-md text-base hover:bg-blue-700"
            >
              {t("monetization.close")}
            </button>
          </div>
        </div>
      </div>
    )}

{/* Modal: ¿Cómo funciona? */}
{showModalHow && (
  <div
    className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-start overflow-y-auto"
    onClick={() => setShowModalHow(false)} // 👉 cierra al clicar fuera
  >
    <div
      className="bg-white rounded-xl shadow-lg p-6 max-w-md w-full mt-10 mb-10 mx-4"
      onClick={(e) => e.stopPropagation()} // 👉 evita cierre al clicar dentro
    >
      <h2 className="text-2xl font-semibold mb-4 text-center">
        {t("monetization.how_title")}
      </h2>

      <div className="text-left mb-4 space-y-2">
        {t("monetization.how_text").split("\n").map((linea, idx) => (
          <p key={idx} className="text-base font-semibold text-yellow-600">
            {linea}
          </p>
        ))}
      </div>

      <div className="text-right">
        <button
          onClick={() => setShowModalHow(false)}
          className="bg-blue-600 text-white px-5 py-2 rounded-md text-base hover:bg-blue-700"
        >
          {t("monetization.close")}
        </button>
      </div>
    </div>
  </div>
)}


{/* Modal d’activation */}
{showIntroModal && (
  <div
    className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-start overflow-y-auto"
    onClick={() => setShowIntroModal(false)} // Cierra al clicar fuera
  >
    <div
      className="bg-white rounded-xl shadow-lg p-6 max-w-md w-full mt-10 mb-10 mx-4"
      onClick={(e) => e.stopPropagation()} // Previene cierre si se clickea dentro
    >
      <h2 className="text-2xl font-bold mb-4 text-center text-green-700">
        {t("earnings.intro.title")}
      </h2>
      <p className="text-sm mb-3 text-gray-800">{t("earnings.intro.description")}</p>
      <p className="text-sm text-gray-600 mb-3">{t("earnings.intro.privacy")}</p>

      <div className="text-sm text-gray-600 mb-4 space-y-3">
        <div>
          <p className="mb-2 font-semibold">{t("earnings.intro.messages_title")}</p>
          <ul className="list-disc pl-5 space-y-1">
            {t("earnings.intro.messages", { returnObjects: true }).map((msg, i) => (
              <li key={i}>{msg}</li>
            ))}
          </ul>
        </div>
        <div>
          <p className="mt-4 mb-2 font-semibold">{t("earnings.intro.earnings_title")}</p>
          <ul className="list-disc pl-5 space-y-1">
            {t("earnings.intro.earnings", { returnObjects: true }).map((line, i) => (
              <li key={i}>{line}</li>
            ))}
          </ul>
        </div>
        <div>
          <p className="mt-4 mb-2 font-semibold">{t("earnings.intro.security_title")}</p>
          <ul className="list-disc pl-5 space-y-1">
            {t("earnings.intro.security", { returnObjects: true }).map((s, i) => (
              <li key={i}>{s}</li>
            ))}
          </ul>
        </div>
      </div>

      <label className="flex items-start gap-2 mb-5 text-sm text-gray-700">
        <input
          type="checkbox"
          checked={consentGiven}
          onChange={(e) => setConsentGiven(e.target.checked)}
          className="mt-1"
        />
        <span>{t("earnings.intro.consent_checkbox")}</span>
      </label>

      <button
        onClick={activarMonetizacion}
        disabled={!consentGiven}
        className={`w-full font-semibold px-6 py-2 rounded-lg transition ${
          consentGiven
            ? "bg-green-600 text-white hover:bg-green-700"
            : "bg-gray-300 text-gray-500 cursor-not-allowed"
        }`}
      >
        {t("earnings.intro.activate_button")}
      </button>
    </div>
  </div>
)}


      {/* Modal de désactivation */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 pt-[100px]">
          <div className="bg-white rounded-xl shadow-lg p-6 max-w-md w-full text-center">
            <h2 className="text-xl font-bold mb-4">{t("earnings.deactivate_modal.title")}</h2>
            <p className="text-gray-700 mb-4">{t("earnings.deactivate_modal.description")}</p>
            <div className="flex justify-center gap-4">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="bg-gray-300 hover:bg-gray-400 px-4 py-2 rounded"
              >
                {t("earnings.deactivate_modal.cancel")}
              </button>
              <button
                onClick={desactivarMonetizacion}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded"
              >
                {t("earnings.deactivate_modal.confirm")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cards intro */}
    <div className="max-w-6xl mx-auto px-6 py-12 text-center">

      <div className="grid gap-6 sm:grid-cols-1 md:grid-cols-2">
        <div
          onClick={() => setShowModalInfo(true)}
          className="cursor-pointer bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition border hover:border-green-400"
        >
          <img
            src="/img/monetisation-info.png"
            alt="Monétisation"
            className="w-32 h-32 mx-auto mb-4"
          />
          <h3 className="text-xl font-semibold text-green-700">
            Monétisation, c’est quoi ?
          </h3>
        </div>

        <div
          onClick={() => setShowModalHow(true)}
          className="cursor-pointer bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition border hover:border-blue-400"
        >
          <img
            src="/img/how-it-works.png"
            alt="Comment ça marche"
            className="w-48 h-32 mx-auto mb-4"
          />
          <h3 className="text-xl font-semibold text-blue-700">
            Comment ça marche ?
          </h3>
        </div>
      </div>


        {/* Stats */}
        <div className="max-w-6xl mx-auto px-6 mt-5">
          <div className="bg-white shadow-lg rounded-2xl p-6 text-center md:text-left">
            <div className="grid md:grid-cols-2 gap-6">

         {/* Columna izquierda: Stats */}
        <div>
          <div className="text-4xl font-bold text-violet-600 mt-4 text-center md:text-left">
            {t("earnings.tokens.total", { amount: availableTokens.toFixed(2) })}
          </div>



          <div className="grid grid-cols-1 gap-6 mt-6">
            <div>
              <h4 className="text-lg font-semibold mb-2">
                {t("earnings.tokens.campaigns_title")}
              </h4>
              <p className="text-gray-600">
                {t("earnings.tokens.campaigns", {
                  count: data.campaign_participation || 0,
                })}
              </p>

            </div>
          </div>
        </div>

        {/* Columna derecha: Botones */}
        <div className="flex flex-col gap-4 items-center justify-center mt-6 md:mt-0">
          <button
            className={`${
              canClaim
                ? "bg-violet-600 hover:bg-violet-700"
                : "bg-violet-300 cursor-not-allowed"
            } text-white font-medium py-2 px-4 rounded-xl shadow transition relative group w-full max-w-xs`}
            onClick={() => canClaim && navigate("/perfil/metodo-pago")}
            disabled={!canClaim}
          >
            {t("earnings.tokens.claim_button")}
            {!canClaim && (
              <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-max max-w-xs bg-black text-white text-xs px-3 py-1 rounded opacity-0 group-hover:opacity-100 transition">
                {t("earnings.tokens.claim_tooltip")}
              </span>
            )}
          </button>

                <button
                  className={`${
                    isActivated
                      ? "bg-red-500 hover:bg-red-600"
                      : "bg-green-600 hover:bg-green-700"
                  } text-white font-medium py-2 px-4 rounded-xl shadow transition w-full max-w-xs`}
                  onClick={() =>
                    isActivated ? setShowConfirmModal(true) : setShowIntroModal(true)
                  }
                >
                  {isActivated
                    ? t("earnings.tokens.disable_button")
                    : "Activer la monétisation"}
                </button>

                <button
                  className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium py-2 px-4 rounded-xl shadow transition w-full max-w-xs"
                  onClick={() => navigate("/monetizacion/historial-retiros")}
                >
                  {t("earnings.tokens.history_button")}
                </button>
              </div>
            </div>

            {/* Footer note */}
            <div className="mt-6 text-xs text-gray-400 text-center leading-5">
              {t("earnings.tokens.footer_note")}
            </div>
          </div>
        </div>


    </div>
    </>
  );
};

export default EarningsPage;
